import {Dispatch, SetStateAction, useState, useCallback, useEffect} from 'react';
import {useConfig} from '@/contexts/config';
import {DocumentModel, updateDocumentModel} from '@/engine';
import {createVisibilitySet, getKeysToUpdate} from '@/engine/document-model/utils';
import {getChangedKeys} from '@/utils/object';
import {useDocumentUtils} from '../utils';
import type {DocumentModelState, UseDocumentModelProps} from './use-document-model';

export function useSubscribeToStoreChange(
  onChanged: UseDocumentModelProps['onChanged'],
  setState: Dispatch<SetStateAction<DocumentModelState>>,
) {
  const {model} = useConfig();
  const {store, valueResolver} = useDocumentUtils();
  const [ready, setReady] = useState(false);

  const computeChanges = useCallback(
    (documentModel: DocumentModel, changedKeys: string[]) => {
      const keysToUpdate = getKeysToUpdate(changedKeys, model.dependencyMap, model.accessMap);

      if (!keysToUpdate.length) return {...documentModel};

      return updateDocumentModel(documentModel, keysToUpdate, model, store, valueResolver);
    },
    [model, store, valueResolver],
  );

  // Subscribe to changes in the store
  useEffect(() => {
    const unsubscribe = store.subscribe((state, prevState) => {
      const changedKeys = getChangedKeys(state.data, prevState.data) as string[];

      if (changedKeys.length === 0) return;

      onChanged?.(changedKeys, state);

      setState(previous => {
        const documentModel = computeChanges(previous.documentModel, changedKeys);
        const visibilitySet = createVisibilitySet(documentModel);
        return {documentModel, visibilitySet};
      });
    });

    setReady(true);

    return () => {
      unsubscribe();
      setReady(false);
    };
  }, [store, computeChanges, valueResolver, onChanged, setReady, setState]);

  return {ready};
}
